import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  InputBase,
  Typography,
  CircularProgress,
  IconButton,
  ClickAwayListener,
  Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Search } from '@mui/icons-material';

import Store from 'js/containers/Search/Store';
import Establishment from 'js/containers/Search/Establishment';
import { getProducts } from 'js/library/utils/API/getProducts';
import { getPromotions } from 'js/library/utils/API/getPromotions';
import { getEstablishment } from 'js/library/utils/API/getEstablishment';
import { trackEventMatomo } from 'js/library/utils/helpers';
import { useCurrentUserInfo } from 'js/library/services/currentUserInfo';
import cfac22 from 'js/library/utils/cfac22';

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
    color: 'black',
    [theme.breakpoints.up('md')]: {
      width: '20ch',
    },
  },
}));

export const SearchBar = () => {
  const { userInfo } = useCurrentUserInfo();
  const [storeList, setStoreList] = React.useState(false);
  const [establishmentList, setEstablishmentList] = React.useState(false);
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [tootipIsOpen, setTooltipIsOpen] = React.useState(false);
  const [label, setLabel] = React.useState('');
  const scrollDebounceRef = React.useRef(null);
  const { t } = useTranslation('app');
  const searchUserId = userInfo?.uId || userInfo?.guestId || userInfo?.triiboId || null;

  const normalizeSearchResult = (result) => (Array.isArray(result) ? result : []);

  async function getData(event) {
    event.preventDefault();

    setEstablishmentList(null);
    setStoreList(null);
    setSearchOpen(true);

    try {
      const payload = {
        userQuery: label,
        productType: ['digital', 'physical'],
        orderType: 'relevance',
        plataform: 'web',
        from: 0,
        size: 50,
        partnerId: cfac22('partnerId'),
      };

      const payloadEstab = {
        ...payload,
        establishmentType: ['digital', 'physical'],
        partnerId: cfac22('partnerId'),
      };
      delete payloadEstab.productType;
      delete payloadEstab.hasVoucher;

      const [promotions, products, establishments] = await Promise.allSettled([
        getPromotions(searchUserId, payload),
        getProducts(searchUserId, payload),
        getEstablishment(searchUserId, payloadEstab),
      ]);

      const hasOnlyRejectedSearches = [promotions, products, establishments].every(
        (request) => request.status === 'rejected'
      );

      if (hasOnlyRejectedSearches) {
        setStoreList('error');
        setEstablishmentList('error');
        return;
      }

      let list = [];

      if (promotions.status === 'fulfilled') {
        list = [...normalizeSearchResult(promotions.value)];
      }

      if (products.status === 'fulfilled') {
        list = [...list, ...normalizeSearchResult(products.value)];
      }

      setEstablishmentList(
        establishments.status === 'fulfilled'
          ? normalizeSearchResult(establishments.value)
          : []
      );

      setStoreList(list);
    } catch (error) {
      console.log(error);
      setStoreList('error');
      setEstablishmentList('error');
    }
  }

  function closeSearchBar() {
    setSearchOpen(false);
  }

  function scrollMatomo() {
    clearTimeout(scrollDebounceRef.current);
    scrollDebounceRef.current = setTimeout(() => {
      trackEventMatomo('Header', 'scroll', 'lista', 'Caixa de pesquisa');
    }, 400);
  }

  return (
    <ClickAwayListener onClickAway={() => setSearchOpen(false)}>
      <Tooltip arrow open={tootipIsOpen} title={t('errors.mandatory_three')}>
        <Box className="search-width">
          <Box
            sx={{
              position: 'relative',
              borderRadius: '27px',
              backgroundColor: '#fff',
              width: '500px',
              maxWidth: '100%',
              '@media (max-width: 900px)': {
                width: '100%',
              },
            }}
          >
            <form
              onSubmit={(e) => {
                if (label.length > 2) {
                  setTooltipIsOpen(false);
                  getData(e);
                } else {
                  e.preventDefault();
                  setTooltipIsOpen(true);
                  setTimeout(() => setTooltipIsOpen(false), 2000);
                }
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  left: 6,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#BABABA',
                  zIndex: 1,
                }}
              >
                <IconButton
                  aria-label={t('nav_bar.search')}
                  type="submit"
                  size="small"
                  sx={{ color: 'inherit' }}
                >
                  <Search />
                </IconButton>
              </Box>

              <StyledInputBase
                id="searchLabel"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder={t('nav_bar.search')}
                inputProps={{ 'aria-label': t('nav_bar.search') }}
              />
            </form>
          </Box>

          {(establishmentList === false && storeList === false) || !searchOpen ? null : (
            <Box className="search-box-container">
              <Box onScroll={scrollMatomo} className="search-box-scroll">
                {storeList === null && establishmentList === null ? (
                  <Box className="search-box" sx={{ textAlign: 'center' }}>
                    <CircularProgress sx={{ padding: 3, boxSizing: 'content-box' }} />
                  </Box>
                ) : storeList === 'error' && establishmentList === 'error' ? (
                  <Box className="search-box" sx={{ textAlign: 'center' }}>
                    <Typography variant="subtitle1">
                      Ocorreu um erro! Por favor tente novamente.
                    </Typography>
                  </Box>
                ) : establishmentList.length === 0 && storeList.length === 0 ? (
                  <Box className="search-box" sx={{ padding: 1, textAlign: 'center' }}>
                    {t('errors.no_data_found')}
                  </Box>
                ) : (
                  <Box>
                    {storeList.map((row, i) => (
                      <Store key={i} func={closeSearchBar} data={row} />
                    ))}
                    {establishmentList.map((row, i) => (
                      <Establishment key={i} func={closeSearchBar} data={row} />
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Tooltip>
    </ClickAwayListener>
  );
};

export default SearchBar;
